import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield } from "lucide-react";

export type AdminStepUpCredentials = {
  password?: string;
  session?: string;
  challengeName?: string;
  mfaCode?: string;
};

export type AdminStepUpConfirmResult =
  | { ok: true }
  | { needsMfa: true; session: string; challengeName: string }
  | { error: string };

type FormProps = {
  title: string;
  description: string;
  confirmLabel?: string;
  /** Called with password or MFA fields. Return needsMfa payload to continue the challenge, or throw/return error. */
  onConfirm: (creds: AdminStepUpCredentials) => Promise<AdminStepUpConfirmResult>;
  /** Cancel / back (does not mean success). */
  onCancel: () => void;
  /** After a successful confirm (ok). */
  onCompleted: () => void;
};

/**
 * Password / MFA fields for admin step-up. Use inside an existing Dialog or via AdminStepUpDialog.
 */
export function AdminStepUpForm({
  title,
  description,
  confirmLabel = "Confirm",
  onConfirm,
  onCancel,
  onCompleted,
}: FormProps) {
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [mfaChallengeName, setMfaChallengeName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPassword("");
    setMfaCode("");
    setMfaSession(null);
    setMfaChallengeName(null);
    setError(null);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const submit = async () => {
    setError(null);
    if (!mfaSession && password.length < 8) {
      setError("Enter your password to continue.");
      return;
    }
    if (mfaSession && mfaCode.trim().length < 6) {
      setError("Enter your MFA code.");
      return;
    }

    setSubmitting(true);
    try {
      const creds: AdminStepUpCredentials =
        mfaSession && mfaChallengeName
          ? {
              session: mfaSession,
              challengeName: mfaChallengeName,
              mfaCode: mfaCode.trim(),
            }
          : { password };

      const result = await onConfirm(creds);
      if ("needsMfa" in result && result.needsMfa) {
        setMfaSession(result.session);
        setMfaChallengeName(result.challengeName);
        setSubmitting(false);
        return;
      }
      if ("error" in result) {
        setError(result.error);
        setSubmitting(false);
        return;
      }
      reset();
      onCompleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {title}
        </DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {!mfaSession ? (
          <div className="space-y-2">
            <Label htmlFor="admin-step-up-password">Your Password</Label>
            <Input
              id="admin-step-up-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Re-enter your password"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="admin-step-up-mfa">Authenticator Or SMS Code</Label>
            <Input
              id="admin-step-up-mfa"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder="6-digit code"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
          </div>
        )}

        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Confirming…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  /** Called with password or MFA fields. Return needsMfa payload to continue the challenge, or throw/return error. */
  onConfirm: (creds: AdminStepUpCredentials) => Promise<AdminStepUpConfirmResult>;
};

export function AdminStepUpDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  onConfirm,
}: Props) {
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        /* Auth step-up: do not dismiss on outside click (looks like a dead field under an overlay). */
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {open ? (
          <AdminStepUpForm
            key="admin-step-up"
            title={title}
            description={description}
            confirmLabel={confirmLabel}
            onConfirm={onConfirm}
            onCancel={() => onOpenChange(false)}
            onCompleted={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Helper for calling /api/users/:id/admin-actions with step-up fields. */
export async function postUserAdminAction(
  userId: string,
  action: string,
  creds: AdminStepUpCredentials,
  extra: Record<string, unknown> = {},
): Promise<
  | { ok: true; data: Record<string, unknown> }
  | { needsMfa: true; session: string; challengeName: string }
  | { error: string }
> {
  const res = await fetch(apiUrl(`/api/users/${encodeURIComponent(userId)}/admin-actions`), {
    method: "POST",
    ...apiFetchInit(),
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...creds, ...extra }),
  });
  const data = (await res.json()) as Record<string, unknown>;
  if (res.ok && data.needsMfa && data.session && data.challengeName) {
    return {
      needsMfa: true,
      session: String(data.session),
      challengeName: String(data.challengeName),
    };
  }
  if (!res.ok) {
    return { error: String(data.error || "Request failed") };
  }
  return { ok: true, data };
}
